import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useUserEmail from "../utils/useUserEmail";
import useLinkedInAccounts, { clearLinkedInHidden } from "../utils/useLinkedInAccounts";
import { useTranslation } from "react-i18next";

export default function SocialCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = useUserEmail();
  const { status, refresh } = useLinkedInAccounts(email);

  const [phase, setPhase] = useState("checking"); // "checking" | "success" | "error"
  const retried = useRef(false);

  // We no longer trust ?auth=success — we ask the backend whether tokens
  // exist for this user. The one complication is timing: the backend may
  // still be writing them when we land here, so a single "disconnected"
  // answer isn't conclusive. Retry once before believing it.
  useEffect(() => {
    if (status === "loading") return;

    if (status === "connected") {
      setPhase("success");
      return;
    }

    if (!retried.current) {
      retried.current = true;
      const timer = setTimeout(refresh, 1500);
      return () => clearTimeout(timer);
    }

    console.error("LinkedIn connect failed:", {
      status,
      error: searchParams.get("error"),
      description: searchParams.get("error_description"),
    });
    setPhase("error");
  }, [status, refresh, searchParams]);

  useEffect(() => {
    if (phase !== "success") return;
    clearLinkedInHidden(email);

    const returnTo = sessionStorage.getItem("afterLinkedInConnect");
    sessionStorage.removeItem("afterLinkedInConnect");

    const timer = setTimeout(() => {
      if (returnTo === "/draft") {
        navigate("/draft", { state: { refresh: true, view: "list" } });
      } else {
        navigate("/create");
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [phase, email, navigate]);

  if (phase === "checking") {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-white gap-4 px-4 text-center">
        <div className="w-8 h-8 border-4 border-primary-400 border-t-transparent rounded-full animate-spin" />
        <p className="text-black-600">{t("socialCallback.checking")}</p>
      </div>
    );
  }

  const isSuccess = phase === "success";

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-white gap-4 px-4 text-center">
      <div className="text-5xl">{isSuccess ? "🎉" : "⚠️"}</div>
      <h2 className="text-2xl font-bold text-black-800">
        {isSuccess
          ? t("socialCallback.successTitle")
          : t("socialCallback.errorTitle")}
      </h2>
      <p className="text-black-600">
        {isSuccess
          ? t("socialCallback.successMessage")
          : t("socialCallback.errorMessage")}
      </p>
      <button
        onClick={() => navigate("/create")}
        className="p-2 bg-black-600 text-white rounded-lg font-bold hover:bg-black-900 focus:outline-none focus:ring-2 focus:ring-primary-400 text-lg border-black-600 hover:border-black-900 border-2 justify-center w-[300px] md:text-md"
      >
        {isSuccess ? t("socialCallback.goBackNow") : t("socialCallback.backToApp")}
      </button>
    </div>
  );
}